import React, { Component, createRef } from 'react'
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import ModelControls from '../../../../../Templates/ModelControls';

class ColorTexturePickerPreview extends Component {
    constructor(props) {
        super(props)
        this.state = {
            modelsLoaded: false,
            selectedPart: null,
            hoveredPart: null,
            previewingTexture: false,
            previewTextureIndex: null,
            saving: false,
            justSaved: false
        }
        
        this.threeJSContainer = createRef();
    }

    normalizeObject = (object) => {
        const targetSize = 10; // All models will be scaled to fit within a 10x10x10 cube.

        // 1. Scale the model to a uniform size.
        const initialBox = new THREE.Box3().setFromObject(object);
        const initialSize = initialBox.getSize(new THREE.Vector3());
        const maxDim = Math.max(initialSize.x, initialSize.y, initialSize.z);
        if (maxDim > 0) {
            const scaleFactor = targetSize / maxDim;
            object.scale.set(scaleFactor, scaleFactor, scaleFactor);
        }

        // 2. Center the scaled model at the origin.
        const scaledBox = new THREE.Box3().setFromObject(object);
        const center = scaledBox.getCenter(new THREE.Vector3());
        object.position.sub(center);

        // 3. Place the model on top of the grid (y=0).
        const finalBox = new THREE.Box3().setFromObject(object);
        object.position.y -= finalBox.min.y;
    }

    frameObject = (object, camera, controls) => {
        const padding = 1.5; // Controls how "zoomed out" the camera is. 1.0 is a tight fit.

        // First, normalize the object's scale and position.
        this.normalizeObject(object);
        
        // Get the model's new size and center point for camera framing.
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Calculate the camera distance required to frame the object.
        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = Math.abs(maxDim / Math.tan(fov / 2)) / 2;

        // Position the camera to achieve a consistent 3/4 viewing angle.
        camera.position.set(
            center.x + cameraDistance,
            center.y + cameraDistance * 0.5,
            center.z + cameraDistance
        ).multiplyScalar(padding);

        // Point the camera at the center of the model.
        controls.target.copy(center);
        controls.update();
    };

    componentDidMount = () => {
        this.initThreeJSViewer();
    }

    componentWillUnmount = () => {
        // Clean up Three.js resources
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        
        if (this.renderer) {
            this.renderer.dispose();
        }
        
        window.removeEventListener('resize', this.handleWindowResize);
        
        if (this.threeJSContainer.current) {
            this.threeJSContainer.current.removeEventListener('click', this.handleModelClick);
            this.threeJSContainer.current.removeEventListener('mousemove', this.handleMouseMove);
        }
    }

    componentDidUpdate(prevProps) {
        // When a texture is selected from the parent component, preview it on the selected part
        if (this.props.selectedTextureIndex !== prevProps.selectedTextureIndex && 
            this.props.selectedTextureIndex !== null) {
            if (this.props.selectedTextureIndex === 'reset') {
                // Handle reset to original
                this.resetToOriginal();
            } else if (this.state.selectedPart) {
                this.previewTextureOnSelectedPart();
            } else {
                // Show a message if no part is selected
                alert('Please select a part on the model first by clicking on it.');
                // Clear the texture selection since no part is selected
                if (this.props.clearTextureSelection) {
                    this.props.clearTextureSelection();
                }
            }
        }
    }

    initThreeJSViewer = () => {
        if (!this.threeJSContainer.current) return;
        
        // Create scene, camera, and renderer
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xf0f0f0);
        
        this.camera = new THREE.PerspectiveCamera(
            45,
            this.threeJSContainer.current.clientWidth / this.threeJSContainer.current.clientHeight,
            0.1,
            1000
        );
        this.camera.position.set(0, 5, 10);
        
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(
            this.threeJSContainer.current.clientWidth,
            this.threeJSContainer.current.clientHeight
        );
        this.renderer.setPixelRatio(window.devicePixelRatio);
        
        this.threeJSContainer.current.appendChild(this.renderer.domElement);
        
        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
        this.scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(1, 1, 1);
        this.scene.add(directionalLight);
        
        // Add orbit controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.screenSpacePanning = false;
        
        // Add grid helper
        const gridHelper = new THREE.GridHelper(100, 100);
        gridHelper.position.y = -0.01;
        this.scene.add(gridHelper);
        this.gridHelper = gridHelper;
        
        // Setup raycaster for click detection
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        
        // Add event listeners
        this.threeJSContainer.current.addEventListener('click', this.handleModelClick);
        this.threeJSContainer.current.addEventListener('mousemove', this.handleMouseMove);
        
        // Load GLB model
        this.loadGLBModel();
        
        // Handle window resize
        window.addEventListener('resize', this.handleWindowResize);
        
        // Animation loop
        const animate = () => {
            this.frameId = requestAnimationFrame(animate);
            this.controls.update();
            this.renderer.render(this.scene, this.camera);
        };
        animate();
    }

    handleMouseMove = (event) => {
        if (this.controls.enabled) {
            const rect = this.threeJSContainer.current.getBoundingClientRect();
            this.mouse.x = ((event.clientX - rect.left) / this.threeJSContainer.current.clientWidth) * 2 - 1;
            this.mouse.y = -((event.clientY - rect.top) / this.threeJSContainer.current.clientHeight) * 2 + 1;
            
            this.raycaster.setFromCamera(this.mouse, this.camera);
            
            const intersects = this.raycaster.intersectObjects(this.scene.children, true);
            const meshIntersections = intersects.filter(intersection => 
                intersection.object.isMesh && 
                intersection.object.userData.isPickable
            );
            
            if (meshIntersections.length > 0) {
                this.threeJSContainer.current.style.cursor = 'crosshair';
                const hoveredObject = meshIntersections[0].object;
                const partGroup = hoveredObject.userData.partGroup;
                
                // Don't apply hover effects if this part is selected and previewing a texture
                const isSelectedAndPreviewing = this.state.selectedPart === partGroup && this.state.previewingTexture;
                
                if (this.state.hoveredPart !== partGroup && !isSelectedAndPreviewing) {
                    this.resetHoverHighlights();
                    this.hoverHighlightPart(partGroup);
                    this.setState({ hoveredPart: partGroup });
                } else if (isSelectedAndPreviewing && this.state.hoveredPart) {
                    // Clear hover state if we're hovering over a selected part with texture preview
                    this.resetHoverHighlights();
                    this.setState({ hoveredPart: null });
                }
            } else {
                this.threeJSContainer.current.style.cursor = 'default';
                if (this.state.hoveredPart) {
                    this.resetHoverHighlights();
                    this.setState({ hoveredPart: null });
                }
            }
        }
    }

    handleModelClick = (event) => {
        const rect = this.threeJSContainer.current.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / this.threeJSContainer.current.clientWidth) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / this.threeJSContainer.current.clientHeight) * 2 + 1;
        
        this.raycaster.setFromCamera(this.mouse, this.camera);
        
        const intersects = this.raycaster.intersectObjects(this.scene.children, true);
        const meshIntersections = intersects.filter(intersection => 
            intersection.object.isMesh && 
            intersection.object.userData.isPickable
        );
        
        if (meshIntersections.length > 0) {
            const objectHit = meshIntersections[0].object;
            const partGroup = objectHit.userData.partGroup;
            
            // If we have a previewed texture and clicking on a different part, revert to original first
            if (this.state.previewingTexture && this.state.selectedPart && this.state.selectedPart !== partGroup) {
                this.revertPreviewToOriginal();
                // Small delay to allow revert to complete before selecting new part
                setTimeout(() => {
                    this.selectNewPart(partGroup);
                }, 100);
                return;
            }
            
            // Check if this part is already selected
            const isAlreadySelected = this.isPartSelected(partGroup);
            
            if (isAlreadySelected) {
                // If we have a previewed texture, revert it, otherwise deselect
                if (this.state.previewingTexture) {
                    this.revertPreviewToOriginal();
                } else {
                    this.deselectPart(partGroup);
                    this.setState({ selectedPart: null });
                }
            } else {
                this.selectNewPart(partGroup);
            }
        } else {
            // Clicked on empty space - do nothing, keep current selection and texture
            // This allows users to keep their component and texture selection when clicking elsewhere
        }
    }

    selectNewPart = (partGroup) => {
        // Deselect previously selected part first
        if (this.state.selectedPart) {
            this.deselectPreviousPart(this.state.selectedPart);
        }
        
        // Select the new part
        this.selectPart(partGroup);
        this.setState({ 
            selectedPart: partGroup,
            previewingTexture: false,
            previewTextureIndex: null
        });
        
        // Clear the texture selection when selecting a different component
        if (this.props.clearTextureSelection) {
            this.props.clearTextureSelection();
        }
    }

    isPartSelected = (partGroup) => {
        let isSelected = false;
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === partGroup && node.userData.isSelected) {
                isSelected = true;
            }
        });
        return isSelected;
    }

    deselectPart = (partGroup) => {
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === partGroup) {
                if (node.userData.hasCustomTexture && node.userData.texturedMaterial) {
                    // Use the stored textured material without selection highlight
                    node.material = node.userData.texturedMaterial.clone();
                } else if (node.userData.originalMaterial) {
                    // Restore the original material
                    node.material = node.userData.originalMaterial.clone();
                }
                node.userData.isSelected = false;
            }
        });
    }

    selectPart = (partGroup) => {
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === partGroup) {
                this.highlightSelectedObject(node);
            }
        });
    }

    hoverHighlightPart = (partGroup) => {
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === partGroup) {
                this.highlightHoveredObject(node);
            }
        });
    }

    resetHoverHighlights = () => {
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.isHovered && !node.userData.isSelected) {
                // Only reset hover if the object is not selected
                if (node.userData.hasCustomTexture && node.userData.texturedMaterial) {
                    // Restore textured material
                    node.material = node.userData.texturedMaterial.clone();
                } else if (node.userData.originalMaterial) {
                    // Restore original material
                    node.material = node.userData.originalMaterial.clone();
                }
                node.userData.isHovered = false;
            }
        });
    }

    highlightSelectedObject = (object) => {
        if (!object.userData.originalMaterial) {
            object.userData.originalMaterial = object.material.clone();
        }
        
        // Base the selection highlight on the current material (textured or original)
        let baseMaterial;
        if (object.userData.hasCustomTexture && object.userData.texturedMaterial) {
            baseMaterial = object.userData.texturedMaterial.clone();
        } else {
            baseMaterial = object.userData.originalMaterial.clone();
        }
        
        const highlightMaterial = baseMaterial;
        highlightMaterial.emissive = new THREE.Color(0xFFFFFF);
        highlightMaterial.emissiveIntensity = 0.3;
        highlightMaterial.needsUpdate = true;
        
        object.material = highlightMaterial;
        object.userData.isSelected = true;
    }

    highlightHoveredObject = (object) => {
        if (!object.userData.originalMaterial) {
            object.userData.originalMaterial = object.material.clone();
        }
        
        // Base the hover highlight on the current material (textured or original)
        let baseMaterial;
        if (object.userData.hasCustomTexture && object.userData.texturedMaterial) {
            baseMaterial = object.userData.texturedMaterial.clone();
        } else {
            baseMaterial = object.userData.originalMaterial.clone();
        }
        
        const highlightMaterial = baseMaterial;
        highlightMaterial.emissive = new THREE.Color(0xFFFFFF);
        highlightMaterial.emissiveIntensity = 0.2;
        highlightMaterial.needsUpdate = true;
        
        object.material = highlightMaterial;
        object.userData.isHovered = true;
    }

    applyTextureToSelectedPart = () => {
        const selectedTexture = this.props.textures[this.props.selectedTextureIndex];
        
        // Load the texture using THREE.js TextureLoader
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
            selectedTexture.path,
            (texture) => {
                // Configure texture properties
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                
                this.scene.traverse((node) => {
                    if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                        // Preserve the original material if not already saved
                        if (!node.userData.originalMaterial) {
                            node.userData.originalMaterial = node.material.clone();
                        }
                        
                        // Create a new textured material based on the original
                        const texturedMaterial = node.userData.originalMaterial.clone();
                        texturedMaterial.map = texture;
                        texturedMaterial.needsUpdate = true;
                        
                        // Store the textured material separately
                        node.userData.texturedMaterial = texturedMaterial;
                        node.userData.hasCustomTexture = true;
                        
                        // Apply the textured material WITHOUT selection highlight
                        node.material = texturedMaterial.clone();
                        node.userData.isSelected = false;
                    }
                });
                
                // Clear the selected part state to remove selection UI
                this.setState({ selectedPart: null });
                
                // Clear the texture selection to allow selecting new textures
                if (this.props.clearTextureSelection) {
                    this.props.clearTextureSelection();
                }
            },
            (progress) => {
                console.log('Texture loading progress:', (progress.loaded / progress.total * 100) + '%');
            },
            (error) => {
                console.error('Error loading texture:', error);
                // Fallback to color-based material if texture loading fails
                this.applyColorFallback(selectedTexture);
            }
        );
    }

    applyColorFallback = (selectedTexture) => {
        // Fallback method using a solid color if texture loading fails
        const fallbackColor = new THREE.Color(0x888888); // Gray fallback
        
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                if (!node.userData.originalMaterial) {
                    node.userData.originalMaterial = node.material.clone();
                }
                
                const texturedMaterial = node.userData.originalMaterial.clone();
                texturedMaterial.color = fallbackColor;
                texturedMaterial.needsUpdate = true;
                
                node.userData.texturedMaterial = texturedMaterial;
                node.userData.hasCustomTexture = true;
                
                node.material = texturedMaterial.clone();
                node.userData.isSelected = false;
            }
        });
        
        this.setState({ selectedPart: null });
        
        // Clear the texture selection to allow selecting new textures
        if (this.props.clearTextureSelection) {
            this.props.clearTextureSelection();
        }
    }

    loadGLBModel = () => {
        const loadingManager = new THREE.LoadingManager();
        const modelId = this.props.model?.temp_model_id || this.props.model?._id;
        const modelPath = this.props.model.path;
        const gltfLoader = new GLTFLoader(loadingManager);
        const modelUrl = `${window.config.getApiBaseUrl()}/api/models/obj/${modelId}/${modelPath}`;
        
        gltfLoader.load(
            modelUrl,
            (gltf) => {
                const object = gltf.scene;
                let partCounter = 0;
                const objectsMap = new Map();
                
                object.traverse((child) => {
                    if (child.isMesh) {
                        if (child.parent && child.parent !== object) {
                            const objName = child.parent.name || `Part_${partCounter}`;
                            if (!objectsMap.has(objName)) {
                                objectsMap.set(objName, []);
                                partCounter++;
                            }
                            objectsMap.get(objName).push(child);
                        } else {
                            const objName = child.name || `Part_${partCounter}`;
                            if (!objectsMap.has(objName)) {
                                objectsMap.set(objName, []);
                                partCounter++;
                            }
                            objectsMap.get(objName).push(child);
                        }
                    }
                });
                
                objectsMap.forEach((meshes, objName) => {
                    meshes.forEach(mesh => {
                        mesh.name = objName;
                        mesh.userData.partGroup = objName;
                        mesh.userData.isPickable = true;
                        mesh.userData.isSelected = false;
                        mesh.userData.isHovered = false;
                    });
                });
                
                // --- Centering and Scaling Logic ---
                this.scene.add(object);
                this.frameObject(object, this.camera, this.controls);

                this.setState({ modelsLoaded: true }, () => {
                    // Load saved texture configurations after model is loaded
                    this.loadSavedTextures();
                });
            },
            (xhr) => {
                const percent = (xhr.loaded / xhr.total) * 100;
                console.log(`Model ${percent.toFixed(2)}% loaded`);
            },
            (error) => {
                console.error('Error loading model:', error);
            }
        );
    }

    handleWindowResize = () => {
        if (!this.threeJSContainer.current) return;
        
        this.camera.aspect = this.threeJSContainer.current.clientWidth / this.threeJSContainer.current.clientHeight;
        this.camera.updateProjectionMatrix();
        
        this.renderer.setSize(
            this.threeJSContainer.current.clientWidth, 
            this.threeJSContainer.current.clientHeight
        );
    }

    // Add helper methods for continuous shift, turn, and zoom (copied from ModelDisplay)
    startContinuousShift = (dirX, dirY) => {
        this.shifting = true;
        this.lastShiftTime = null;
        const shiftAnimate = (time) => {
            if (!this.shifting) {
                this.lastShiftTime = null;
                return;
            }
            if (this.lastShiftTime === null) {
                this.lastShiftTime = time;
            }
            const deltaTime = time - this.lastShiftTime;
            const speed = 0.003;
            const distance = speed * deltaTime;
            const forward = new THREE.Vector3();
            this.camera.getWorldDirection(forward);
            const right = new THREE.Vector3();
            right.crossVectors(forward, this.camera.up).normalize();
            const up = this.camera.up.clone().normalize();
            const shift = new THREE.Vector3();
            shift.addScaledVector(right, dirX * distance);
            shift.addScaledVector(up, dirY * distance);
            this.camera.position.add(shift);
            if (this.controls) {
                this.controls.target.add(shift);
            }
            this.lastShiftTime = time;
            requestAnimationFrame(shiftAnimate);
        };
        requestAnimationFrame(shiftAnimate);
    };

    stopContinuousShift = () => {
        this.shifting = false;
    };

    onShiftUp = () => { this.startContinuousShift(0, 1); }
    onShiftDown = () => { this.startContinuousShift(0, -1); }
    onShiftLeft = () => { this.startContinuousShift(-1, 0); }
    onShiftRight = () => { this.startContinuousShift(1, 0); }
    onShiftStop = () => { this.stopContinuousShift(); }

    rotateLeft = (angle) => {
        const axis = new THREE.Vector3(0, 1, 0);
        const offset = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
        offset.applyAxisAngle(axis, angle);
        this.camera.position.copy(this.controls.target).add(offset);
        this.camera.lookAt(this.controls.target);
    };

    rotateUp = (angle) => {
        const right = new THREE.Vector3();
        right.crossVectors(this.camera.getWorldDirection(new THREE.Vector3()), this.camera.up).normalize();
        const offset = new THREE.Vector3().subVectors(this.camera.position, this.controls.target);
        offset.applyAxisAngle(right, angle);
        this.camera.position.copy(this.controls.target).add(offset);
        this.camera.lookAt(this.controls.target);
    };

    startContinuousTurn = (direction) => {
        this.turning = true;
        this.lastTurnTime = null;
        const turnAnimate = (time) => {
            if (!this.turning) {
                this.lastTurnTime = null;
                return;
            }
            if (this.lastTurnTime === null) {
                this.lastTurnTime = time;
            }
            const deltaTime = time - this.lastTurnTime;
            const angularSpeed = 0.001;
            const angle = angularSpeed * deltaTime;
            switch(direction) {
                case 'up':
                    this.rotateUp(-angle);
                    break;
                case 'down':
                    this.rotateUp(angle);
                    break;
                case 'left':
                    this.rotateLeft(-angle);
                    break;
                case 'right':
                    this.rotateLeft(angle);
                    break;
                default:
                    break;
            }
            this.lastTurnTime = time;
            requestAnimationFrame(turnAnimate);
        };
        requestAnimationFrame(turnAnimate);
    };

    stopContinuousTurn = () => {
        this.turning = false;
    };

    onTurnUp = () => { this.startContinuousTurn('up'); }
    onTurnDown = () => { this.startContinuousTurn('down'); }
    onTurnLeft = () => { this.startContinuousTurn('left'); }
    onTurnRight = () => { this.startContinuousTurn('right'); }
    onTurnStop = () => { this.stopContinuousTurn(); }

    startContinuousZoom = (direction) => {
        this.zooming = true;
        this.lastZoomTime = null;
        const zoomAnimate = (time) => {
            if (!this.zooming) {
                this.lastZoomTime = null;
                return;
            }
            if (this.lastZoomTime === null) {
                this.lastZoomTime = time;
            }
            const deltaTime = time - this.lastZoomTime;
            const zoomSpeed = 0.005;
            const move = zoomSpeed * deltaTime;
            const vec = new THREE.Vector3();
            vec.subVectors(this.controls.target, this.camera.position).normalize();
            if (direction === 'in') {
                this.camera.position.addScaledVector(vec, move);
            } else if (direction === 'out') {
                this.camera.position.addScaledVector(vec, -move);
            }
            this.camera.lookAt(this.controls.target);
            this.lastZoomTime = time;
            requestAnimationFrame(zoomAnimate);
        };
        requestAnimationFrame(zoomAnimate);
    };

    stopContinuousZoom = () => {
        this.zooming = false;
    };

    onZoomInDown = () => { this.startContinuousZoom('in'); }
    onZoomOutDown = () => { this.startContinuousZoom('out'); }
    onZoomStop = () => { this.stopContinuousZoom(); }

    deselectPreviousPart = (partGroup) => {
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.partGroup === partGroup) {
                // Only restore original color if no custom texture was applied
                if (node.userData.originalMaterial && !node.userData.hasCustomTexture) {
                    node.material = node.userData.originalMaterial.clone();
                } else if (node.userData.hasCustomTexture && node.userData.texturedMaterial) {
                    // Use the stored textured material without selection highlight
                    node.material = node.userData.texturedMaterial.clone();
                }
                node.userData.isSelected = false;
            }
        });
    }

    previewTextureOnSelectedPart = () => {
        const selectedTexture = this.props.textures[this.props.selectedTextureIndex];
        
        // Load the texture using THREE.js TextureLoader
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
            selectedTexture.path,
            (texture) => {
                // Configure texture properties
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;
                texture.repeat.set(1, 1);
                
                this.scene.traverse((node) => {
                    if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                        // Preserve the original material if not already saved
                        if (!node.userData.originalMaterial) {
                            node.userData.originalMaterial = node.material.clone();
                        }
                        
                        // Create a preview material with texture but NO selection highlight
                        const previewMaterial = node.userData.originalMaterial.clone();
                        previewMaterial.map = texture;
                        previewMaterial.needsUpdate = true;
                        
                        // Apply the preview material without highlight
                        node.material = previewMaterial;
                        node.userData.isSelected = true; // Keep track that it's selected but don't highlight
                    }
                });
                
                // Set preview state
                this.setState({ 
                    previewingTexture: true,
                    previewTextureIndex: this.props.selectedTextureIndex
                });
            },
            (progress) => {
                console.log('Texture preview loading progress:', (progress.loaded / progress.total * 100) + '%');
            },
            (error) => {
                console.error('Error loading texture preview:', error);
            }
        );
    }

    commitPreviewedTexture = async () => {
        if (this.state.previewingTexture && this.state.selectedPart) {
            const selectedTexture = this.props.textures[this.state.previewTextureIndex];
            
            // Load the texture again for permanent application
            const textureLoader = new THREE.TextureLoader();
            textureLoader.load(
                selectedTexture.path,
                async (texture) => {
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    texture.repeat.set(1, 1);
                    
                    this.scene.traverse((node) => {
                        if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                            // Create permanent textured material
                            const texturedMaterial = node.userData.originalMaterial.clone();
                            texturedMaterial.map = texture;
                            texturedMaterial.needsUpdate = true;
                            
                            // Store the textured material separately
                            node.userData.texturedMaterial = texturedMaterial;
                            node.userData.hasCustomTexture = true;
                            
                            // Store texture information for persistence
                            node.userData.appliedTexturePath = selectedTexture.path;
                            node.userData.appliedTextureIndex = this.state.previewTextureIndex;
                            node.userData.appliedTextureName = selectedTexture.name;
                            
                            // Apply without selection highlight
                            node.material = texturedMaterial.clone();
                            node.userData.isSelected = false;
                        }
                    });
                    
                    // Clear states
                    this.setState({ 
                        selectedPart: null,
                        previewingTexture: false,
                        previewTextureIndex: null
                    });
                    
                    // Clear the texture selection
                    if (this.props.clearTextureSelection) {
                        this.props.clearTextureSelection();
                    }
                    
                    // Remove automatic save - only save when user clicks save button manually
                    // await this.saveTextureConfiguration();
                }
            );
        }
    }

    // Add new methods for saving and loading texture configurations
    getTextureConfiguration = () => {
        const textureConfig = [];
        
        console.log('Getting texture configuration from scene...');
        
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.hasCustomTexture && node.userData.appliedTexturePath) {
                console.log('Found textured part:', node.userData.partGroup, 'with texture:', node.userData.appliedTextureName);
                textureConfig.push({
                    partGroup: node.userData.partGroup,
                    texturePath: node.userData.appliedTexturePath,
                    textureIndex: node.userData.appliedTextureIndex,
                    textureName: node.userData.appliedTextureName
                });
            }
        });
        
        console.log('Total texture configurations found:', textureConfig.length);
        
        return textureConfig;
    }

    saveTextureConfiguration = async () => {
        const textureConfig = this.getTextureConfiguration();
        const attachmentId = this.props.model?.temp_model_id || this.props.model?._id;
        
        if (!attachmentId) {
            console.log('No attachment ID to save textures to');
            return;
        }
        
        // Allow saving even if textureConfig is empty (to clear all textures)
        console.log(`Saving ${textureConfig.length} texture configuration(s)...`);
        
        this.setState({ saving: true, justSaved: false });
        
        try {
            const response = await fetch(`${window.config.getApiBaseUrl()}/api/attachments/${attachmentId}/texture-config`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
                },
                body: JSON.stringify({ textureConfig })
            });
            
            if (response.ok) {
                console.log('Texture configuration saved successfully');
                this.setState({ saving: false, justSaved: true });
                
                // Notify parent component that textures were saved
                if (this.props.onTexturesSaved) {
                    this.props.onTexturesSaved();
                }
                
                // Reset the success state after 2 seconds
                setTimeout(() => {
                    this.setState({ justSaved: false });
                }, 2000);
            } else {
                console.error('Failed to save texture configuration:', response.statusText);
                this.setState({ saving: false });
            }
        } catch (error) {
            console.error('Error saving texture configuration:', error);
            this.setState({ saving: false });
        }
    }

    loadSavedTextures = async () => {
        const attachmentId = this.props.model?.temp_model_id || this.props.model?._id;
        
        if (!attachmentId) {
            console.log('No attachment ID for loading textures');
            return;
        }

        console.log('Loading saved textures for attachment ID:', attachmentId);

        // ALWAYS reset all materials to default first to ensure clean state
        console.log('Resetting all materials to default before loading saved textures...');
        this.scene.traverse((node) => {
            if (node.isMesh && node.userData.isPickable) {
                const defaultMaterial = new THREE.MeshPhongMaterial({ color: 0xffffff });
                
                // Dispose of old textures to free memory
                if (node.material && node.material.map) {
                    node.material.map.dispose();
                }
                
                node.material = defaultMaterial;
                node.userData.originalMaterial = defaultMaterial.clone(); 
                node.userData.hasCustomTexture = false;
                node.userData.texturedMaterial = null;
                node.userData.appliedTexturePath = null;
                node.userData.appliedTextureIndex = null;
                node.userData.appliedTextureName = null;
                node.userData.isSelected = false; 
                node.userData.isHovered = false;
            }
        });
        
        try {
            const response = await fetch(`${window.config.getApiBaseUrl()}/api/attachments/${attachmentId}/texture-config`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                let effectiveTextureConfig = [];

                if (data && data.textureConfig) {
                    effectiveTextureConfig = typeof data.textureConfig === 'string' 
                        ? JSON.parse(data.textureConfig) 
                        : data.textureConfig;
                    // Ensure effectiveTextureConfig is an array, even if parsing resulted in something else
                    if (!Array.isArray(effectiveTextureConfig)) {
                        effectiveTextureConfig = [];
                    }
                }
                    
                if (effectiveTextureConfig.length > 0) {
                    console.log('Applying saved texture configuration:', effectiveTextureConfig);
                    this.applyStoredTextures(effectiveTextureConfig);
                } else {
                    console.log('No saved texture configuration found, keeping default materials.');
                }
            } else {
                console.log('No saved texture configuration found (response not OK), keeping default materials.');
            }
        } catch (error) {
            console.error('Error loading texture configuration:', error);
        }
    }

    applyStoredTextures = (textureConfig) => {
        const textureLoader = new THREE.TextureLoader();
        
        console.log('Applying stored textures for', textureConfig.length, 'parts...');
        
        let loadedCount = 0;
        const totalTextures = textureConfig.length;
        
        textureConfig.forEach(config => {
            console.log('Loading texture for part:', config.partGroup, 'texture:', config.textureName);
            
            textureLoader.load(
                config.texturePath,
                (texture) => {
                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    texture.repeat.set(1, 1);
                    
                    console.log('Successfully loaded texture for:', config.partGroup);
                    
                    this.scene.traverse((node) => {
                        if (node.isMesh && node.userData.partGroup === config.partGroup) {
                            // Preserve original material if not already saved
                            if (!node.userData.originalMaterial) {
                                node.userData.originalMaterial = node.material.clone();
                            }
                            
                            // Create textured material based on the original
                            const texturedMaterial = new THREE.MeshPhongMaterial({
                                map: texture,
                                color: 0xffffff // Ensure base color is white to show texture properly
                            });
                            texturedMaterial.needsUpdate = true;
                            
                            // Store texture information for persistence
                            node.userData.texturedMaterial = texturedMaterial;
                            node.userData.hasCustomTexture = true;
                            node.userData.appliedTexturePath = config.texturePath;
                            node.userData.appliedTextureIndex = config.textureIndex;
                            node.userData.appliedTextureName = config.textureName;
                            
                            // Apply the texture immediately
                            node.material = texturedMaterial.clone();
                            
                            console.log('Applied texture to:', config.partGroup);
                        }
                    });
                    
                    // Increment loaded count and force render when all textures are loaded
                    loadedCount++;
                    if (loadedCount === totalTextures) {
                        console.log('All textures loaded, forcing render...');
                        if (this.renderer && this.scene && this.camera) {
                            this.renderer.render(this.scene, this.camera);
                        }
                    }
                },
                (progress) => {
                    console.log('Loading progress for', config.partGroup, ':', (progress.loaded / progress.total * 100) + '%');
                },
                (error) => {
                    console.error(`Error loading saved texture for ${config.partGroup}:`, error);
                    // Still increment count to prevent hanging
                    loadedCount++;
                    if (loadedCount === totalTextures) {
                        console.log('All texture loading attempts completed (some may have failed), forcing render...');
                        if (this.renderer && this.scene && this.camera) {
                            this.renderer.render(this.scene, this.camera);
                        }
                    }
                }
            );
        });
    }

    resetToOriginal = () => {
        if (this.state.selectedPart) {
            console.log('[resetToOriginal] Resetting texture for part:', this.state.selectedPart);
            
            this.scene.traverse((node) => {
                if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                    console.log('[resetToOriginal] Found node:', node.userData.partGroup);
                    console.log('[resetToOriginal] Current material has texture:', !!node.material.map);
                    console.log('[resetToOriginal] originalMaterial exists:', !!node.userData.originalMaterial);
                    console.log('[resetToOriginal] originalMaterial has texture:', !!node.userData.originalMaterial?.map);
                    
                    if (node.userData.originalMaterial) {
                        // Dispose of the old texture if it exists
                        if (node.material && node.material.map) {
                            node.material.map.dispose();
                        }
                        
                        // Reset to original material WITHOUT selection highlight
                        node.material = node.userData.originalMaterial.clone();
                        node.material.needsUpdate = true;
                        
                        // Clear ALL texture-related metadata so it won't be saved
                        node.userData.hasCustomTexture = false;
                        node.userData.texturedMaterial = null;
                        node.userData.appliedTexturePath = null;
                        node.userData.appliedTextureIndex = null;
                        node.userData.appliedTextureName = null;
                        node.userData.isSelected = false; // Deselect after reset
                        
                        console.log('[resetToOriginal] Reset complete. New material has texture:', !!node.material.map);
                    }
                }
            });
            
            // Clear states
            this.setState({ 
                selectedPart: null,
                previewingTexture: false,
                previewTextureIndex: null
            });
            
            // Clear the texture selection
            if (this.props.clearTextureSelection) {
                this.props.clearTextureSelection();
            }
            
            // Don't auto-save - let user click "Save Textures" button manually
            console.log('[resetToOriginal] Reset complete. Click "Save Textures" to persist this change.');
        }
    }

    revertPreviewToOriginal = () => {
        if (this.state.previewingTexture && this.state.selectedPart) {
            this.scene.traverse((node) => {
                if (node.isMesh && node.userData.partGroup === this.state.selectedPart) {
                    if (node.userData.originalMaterial) {
                        // Restore original material with selection highlight
                        const highlightedOriginal = node.userData.originalMaterial.clone();
                        highlightedOriginal.emissive = new THREE.Color(0xFFFFFF);
                        highlightedOriginal.emissiveIntensity = 0.3;
                        highlightedOriginal.needsUpdate = true;
                        
                        node.material = highlightedOriginal;
                        node.userData.isSelected = true;
                    }
                }
            });
            
            // Clear preview state but keep part selected
            this.setState({ 
                previewingTexture: false,
                previewTextureIndex: null
            });
            
            // Clear the texture selection
            if (this.props.clearTextureSelection) {
                this.props.clearTextureSelection();
            }
        }
    }

    render() {
        return (
            <div id="color-texture-picker-container" className="flex-centered max-size" style={{ position: 'relative' }}>
                {/* Selected Part Info */}
                {this.state.selectedPart ? (
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '15px 20px',
                        borderRadius: '20px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#081F5F',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <div>
                            Selected: {this.state.selectedPart}
                            {this.state.previewingTexture && (
                                <span style={{ color: '#FF6B35', marginLeft: '10px' }}>
                                    (Previewing: {this.props.textures[this.state.previewTextureIndex]?.name})
                                </span>
                            )}
                        </div>
                        
                        {this.state.previewingTexture && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={this.commitPreviewedTexture}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#081F5F',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '15px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Apply Texture
                                </button>
                            </div>
                        )}
                        
                        {!this.state.previewingTexture && (
                            <div style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
                                Select a texture to preview • Click elsewhere to deselect
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '10px 20px',
                        borderRadius: '20px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        fontSize: '14px',
                        color: '#666'
                    }}>
                        Click on a model part to select it
                    </div>
                )}
                
                <div 
                    id="webGL-window" 
                    className="flex-centered max-size"
                    ref={this.threeJSContainer}
                    style={{ width: '100%', height: '100%' }}
                >
                    <ModelControls 
                        onShiftUp={this.onShiftUp}
                        onShiftDown={this.onShiftDown}
                        onShiftLeft={this.onShiftLeft}
                        onShiftRight={this.onShiftRight}
                        onShiftStop={this.onShiftStop} 
                        onTurnUp={this.onTurnUp}
                        onTurnDown={this.onTurnDown}
                        onTurnLeft={this.onTurnLeft}
                        onTurnRight={this.onTurnRight}
                        onTurnStop={this.onTurnStop}
                        onZoomInDown={this.onZoomInDown}
                        onZoomOutDown={this.onZoomOutDown}
                        onZoomStop={this.onZoomStop}
                    />
                </div>
                
                {/* Save Textures Button */}
                <button
                    onClick={this.saveTextureConfiguration}
                    disabled={this.state.saving}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        padding: '12px 20px',
                        backgroundColor: this.state.justSaved ? '#4CAF50' : this.state.saving ? '#FFA726' : '#081F5F',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        cursor: this.state.saving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        transition: 'all 0.3s ease',
                        opacity: this.state.saving ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                        if (!this.state.saving && !this.state.justSaved) {
                            e.target.style.backgroundColor = '#0a1a4d';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!this.state.saving && !this.state.justSaved) {
                            e.target.style.backgroundColor = '#081F5F';
                        }
                    }}
                >
                    {this.state.saving ? 'Saving...' : this.state.justSaved ? 'Saved!' : 'Save Textures'}
                </button>
            </div>
        );
    }
}

export default ColorTexturePickerPreview; 